import { JOB_SCHEDULE, fmtDayTime, fmtDuration } from '@remote-medium/shared';
import { unwrap, useApi } from '@remote-medium/ui';
import { Send } from 'lucide-react';
import { useLocation } from 'react-router';

import { admin } from '../../lib/client.ts';
import { RunStatusChip } from '../../lib/status-chip.tsx';
import {
  DataTable,
  EmptyRow,
  LinkButton,
  None,
  Notice,
  Page,
  PageHeader,
  SectionHeader,
  StatusRow,
  tableReady,
} from '../../lib/ui.tsx';

const JOBS: Record<string, { title: string; cadence: string }> = {
  rank: { title: 'Ranking', cadence: JOB_SCHEDULE.rank.cadence },
  freshness: { title: 'Freshness', cadence: JOB_SCHEDULE.freshness.cadence },
  digest: { title: 'Digest', cadence: JOB_SCHEDULE.digest.cadence },
};

const humanize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ');

export function OpsPage() {
  const runs = useApi(() => unwrap(admin['job-runs'].$get()), []);
  const emails = useApi(() => unwrap(admin['email-log'].$get({ query: {} })), []);
  const location = useLocation();
  const digestMsg = (location.state as { digestMsg?: string } | null)?.digestMsg;

  return (
    <Page>
      <PageHeader
        title="Ops"
        description="Every scheduled or triggered job writes a run below, and every email is logged. The digest has no automatic cadence yet; early role volume is too low for a reliable weekly send."
        actions={
          <LinkButton to="/ops/digest-preview" variant="outline">
            <Send data-icon="inline-start" />
            Preview digest
          </LinkButton>
        }
      />

      {digestMsg && <Notice>{digestMsg}</Notice>}

      <section className="flex flex-col gap-3">
        <SectionHeader title="Job runs" aside="Latest first" />
        <DataTable>
          <thead>
            <tr>
              <th>Job</th>
              <th>Status</th>
              <th>Started</th>
              <th>Took</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            <StatusRow colSpan={5} loading={runs.loading} error={runs.error} />
            {tableReady(runs) && runs.data?.length === 0 && (
              <EmptyRow colSpan={5}>No runs recorded yet.</EmptyRow>
            )}
            {(tableReady(runs) ? (runs.data ?? []) : []).map((r) => {
              const job = JOBS[r.name];
              const counts = Object.entries(r.counts ?? {});
              return (
                <tr key={r.id} className="hover:bg-muted/50">
                  <td>
                    <div className="font-semibold">{job?.title ?? humanize(r.name)}</div>
                    {job && <div className="text-xs text-muted-foreground">{job.cadence}</div>}
                  </td>
                  <td>
                    <RunStatusChip status={r.status} />
                  </td>
                  <td className="whitespace-nowrap text-muted-foreground tabular-nums">
                    {fmtDayTime(r.startedAt)}
                  </td>
                  <td className="whitespace-nowrap text-muted-foreground tabular-nums">
                    {fmtDuration(r.startedAt, r.finishedAt) ?? <None>…</None>}
                  </td>
                  <td className="max-w-96">
                    {r.error ? (
                      <span className="text-destructive">{r.error}</span>
                    ) : counts.length ? (
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground">
                        {counts.map(([k, v]) => (
                          <span key={k} className="whitespace-nowrap">
                            {humanize(k)}{' '}
                            <span className="font-medium text-foreground tabular-nums">{v}</span>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <None />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </DataTable>
      </section>

      <section className="flex flex-col gap-3">
        <SectionHeader
          title="Email log"
          aside="Sent = handed to Resend · Logged = console stub in dev · Failed = provider error"
        />
        <DataTable>
          <thead>
            <tr>
              <th>To</th>
              <th>Template</th>
              <th>Status</th>
              <th>Provider id</th>
              <th>When</th>
            </tr>
          </thead>
          <tbody>
            <StatusRow colSpan={5} loading={emails.loading} error={emails.error} />
            {tableReady(emails) && emails.data?.length === 0 && (
              <EmptyRow colSpan={5}>No emails yet.</EmptyRow>
            )}
            {(tableReady(emails) ? (emails.data ?? []) : []).map((e) => (
              <tr key={e.id} className="hover:bg-muted/50">
                <td className="max-w-72 truncate" title={e.toEmail}>
                  {e.toEmail}
                </td>
                <td className="whitespace-nowrap">{humanize(e.template)}</td>
                <td>
                  <RunStatusChip status={e.status} />
                </td>
                <td className="max-w-48 truncate font-mono text-xs text-muted-foreground">
                  {e.providerMessageId ?? <None>—</None>}
                </td>
                <td className="whitespace-nowrap text-muted-foreground tabular-nums">
                  {fmtDayTime(e.sentAt)}
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      </section>
    </Page>
  );
}
